import * as React from 'react';
import { memo } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';

import { cn, useResponsiveBreakPoints, itemListProps } from '@restworld/utils-common';

import { JobShiftsType } from './job-shifts.types';
import { Wrapper } from '@restworld/ui-components';
import { PrimaryList } from '../list/list.primary';
import { Heading, Text } from '@restworld/ui-ds-base';
import { ClockIcon } from '@heroicons/react/24/outline';
import { useJobShiftsList } from './job-shifts.hooks';

export const JobShifts = memo(
  ({
    jobType,
    daysRequiredPerWeek,
    daysRequiredPerWeekNumber,
    shifts,
    shiftNotes,
    partTimeWeeklyHours,
    children: applicationEntryPointButtonAsChild,
  }: JobShiftsType) => {
    const { isDesktop } = useResponsiveBreakPoints();
    const { t } = useTranslation('common');
    const jobShiftList = useJobShiftsList({ daysRequiredPerWeek, daysRequiredPerWeekNumber, shifts });
    return (
      <Wrapper>
        <span key="main-title" className="flex items-center gap-1">
          <ClockIcon className="h-6 w-6" />
          <Heading size={4} weight="semibold">
            {t('hours')}
          </Heading>
        </span>
        {jobType && (
          <Heading key="job-time-title" as="span" size={5} weight="semibold">
            {`${t('commitment')} ${jobType.toLowerCase()}:`}
          </Heading>
        )}
        {/* Its own line, not appended to the heading: the longest value in
            production is 35 characters in capitals, and the heading is the
            narrowest thing in an already crowded block. */}
        {partTimeWeeklyHours && (
          <Text key="part-time-weekly-hours" className="text-gray-700">
            {partTimeWeeklyHours}
          </Text>
        )}
        <JobShiftMainContent itemList={jobShiftList} shiftNotes={shiftNotes} />
        {!isDesktop && <>{applicationEntryPointButtonAsChild}</>}
      </Wrapper>
    );
  }
);
export default JobShifts;
const JobShiftMainContent = ({ itemList, shiftNotes = '' }: { itemList: itemListProps[]; shiftNotes?: string }) => {
  const { isDesktop } = useResponsiveBreakPoints();
  const shiftNoteEl = shiftNotes ? (
    <ParagraphWrapper>
      {isDesktop && <InformationCircleIconStyled />}
      <Text key="launch-available-if-early">{shiftNotes}</Text>
    </ParagraphWrapper>
  ) : null;
  return (
    <JobShiftContentWrapper>
      <PrimaryList key="time-table-list" itemList={itemList} type="default" />
      {shiftNoteEl}
    </JobShiftContentWrapper>
  );
};
const JobShiftContentWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex gap-2 flex-col md:flex-row md:w-full md:justify-between', className)} {...props} />
);
const ParagraphWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('md:w-2/5', className)} {...props} />
);
const InformationCircleIconStyled = (props: React.ComponentProps<typeof InformationCircleIcon>) => (
  <InformationCircleIcon {...props} className={cn('h-6 w-6 text-primary-800 mb-2', props.className)} />
);
